import { Feature, FeatureCollection, MultiPolygon, Point } from "geojson";
import { IrrigationSystem } from "./irrigationSystem";
import { ViewType } from "./enums";

export type NStatus = "red" | "yellow" | "green";
export enum ImageryExtent {
    FIELD = 1,
    IRRIGATED_REGION = 2,
}
export interface FieldRecommendation {
    recent_application?: string;
    gap: boolean;
    recommend_fertigation: null | boolean;
    n_status: null | NStatus;
    prescription_id?: number;
    recommendation_date?: string;
    pending_application: boolean;
    percent_sufficient?: number;
    si_mean?: number;
    insights_active?: boolean;
    days_to_apply?: number;
    imagery_id?: number;
    latest_image_capture_date: string | null;
    user_recommendation?: {
        recommendation_date: string;
        recommend_application: boolean | null;
        recommend_irrigation: boolean | null;
        notes: string | null;
        days_active: number | null;
        user: string;
    } | null;
}

export interface Field {
    id: number;
    name: string;
    is_paid: boolean;
    is_billed: boolean;
    filter_soil: boolean;
    created_by: number;
    last_modified_by: number;
    grower: string | number;
    users: { user_id: number; view_type: ViewType }[];
    center: number[];
    typical_N_applied_pounds_per_acre: null;
    typical_yield_bushels_per_acre: null;
    soil_configuration: number | null;
    imagery_extent: number;
    recommendation_resolution_level: null;
    insight_sensitivity?: "low" | "moderate" | "high";
    insights_active_manual: boolean | null;
    notifications_active_manual: boolean | null;
    lockout_days: number;
    max_n_savings_pounds_per_acre: number;
    n_reference_offset: number;
    fertigation_use: boolean;
    acres: number;
    acres_enrolled: number;
    recommendation: FieldRecommendation;
    trend: null;
    enrollment_tier: number;
    enrollment_active: boolean;
    latest_image: {
        id: number;
        created_by: number;
        last_modified_by: number;
        created_date: string;
        capture_date: string;
        container: string;
        folder: string;
        status: number;
        n_status: null;
        favorite: boolean;
        flag: boolean;
        recommend_fertigation: null;
        days_to_apply?: number;
    };
    latest_complete_application_operation: {
        id: number;
        start_date: string;
        end_date: string;
        created_by: null;
        last_modified_by: null;
        provider: string;
        source_id: string;
        average_N: number;
        note: null;
        application_method: string;
        application_products: number[];
        prescription: number;
        technique: string;
        is_complete: boolean;
    };
    crop?: {
        id: string;
        name: string;
    };
    active_enrollment: Enrollment;

    default_n_rate?: number;
    default_product?: number;
    default_depth?: number;
    irrigation_systems: IrrigationSystem[];
    farm?: {
        id: number;
        name: string;
        group_type: string;
    };
    service_provider?: {
        id: number;
        name: string;
        group_type: string;
        account_manager?: {
            id: number;
            full_name: string;
            email: string;
        };
    };
    crm_id?: number;
    default_max_speed_percentage?: number;
    active_planting_date?: string;
    soil_organic_matter: number | null;
    has_drainage: boolean;
    active_target_growth_stage: number | null;
    virtual_SI_mode: boolean;
    analysis_mode: AnalysisMode;
    water_holding_capacity: number | null;
    wilting_point: number | null;
    target_moisture_min: number | null;
    target_moisture_max: number | null;
    depth_to_manage_water: number | null;
    initial_soil_nitrogen: number;
}

export enum AnalysisMode {
    HARMONIZED = "harmonized",
    STANDARD_SENSITIZED = "standard_sensitized",
    POWER = "power",
    EXPONENTIAL = "exponential",
    DEFICIT_POWER = "deficit_power",
}

export interface FieldGeometry {
    field: Feature<MultiPolygon, { center: number[] }>;
    irrigation_systems: FeatureCollection<
        MultiPolygon,
        {
            pivot_start_degree: number;
            pivot_stop_degree: number;
            pivot_center: Point;
            name: string;
        }
    >;
    treatment_sections: FeatureCollection<
        MultiPolygon,
        {
            irrigation_system: number;
            start_degree: number;
            stop_degree: number;
            treatment: string;
            created_by: number;
            last_modified_by: number;
            section_number: number;
        }
    >;
    management_zones: FeatureCollection<
        MultiPolygon,
        {
            created_by: number;
            last_modified_by: number;
            layer: number;
            zone_number: number;
        }
    >;
    prescribed_plots: FeatureCollection<
        MultiPolygon,
        {
            pair_id: number;
            plot_type: number;
            rate_offset: number;
            created_by: null | number;
            last_modified_by: null | number;
            field: number;
            irrigation_system: null;
            prescription: number;
            start_degree: null;
            stop_degree: null;
            zone: null;
            type: string;
            is_active: boolean;
        }
    >;
    analytic_plots: FeatureCollection<
        MultiPolygon,
        {
            pair_id: number;
            plot_type: number;
            rate_offset: number;
            created_by: null | number;
            created_date: string;
            last_modified_by: null | number;
            field: number;
            irrigation_system: null;
            prescription: number;
            start_degree: null;
            stop_degree: null;
            zone: null;
            type: string;
            is_active: boolean;
            latest_applied_date: null;
            latest_application_id: null;
            prescription_name: string;
        }
    >;
}

export interface FieldSummary {
    id: number;
    status: number;
    name: string;
    grower: {
        id: number;
        name: string;
        group_type: string;
    };
    center: number[] | null;
    imagery_extent: number;
    recommendation_resolution_level: null | number;
    enrollment_active: boolean;
    enrollment_tier: number;
    active_enrollment: Enrollment;
    service_provider?: {
        id: number;
        name: string;
        group_type: string;
        account_manager?: {
            id: number;
            full_name: string;
            email: string;
        };
    };
}

export type EnrollmentFormField = {
    id: number;
    name: string;
    imagery_boundary_acres: number;
    active_enrollment?: Enrollment;
    grower: {
        name?: string;
        service_provider?: {
            id: number;
            name?: string;
        };
    };
};

export interface FieldWithEnrollment extends EnrollmentFormField {
    grower: {
        id: number;
        name: string;
        group_type: string;
        service_provider?: {
            id: number;
            name: string;
            group_type: string;
        };
    };
    status: number;
    center: number[];
    imagery_extent: number;
    enrollments: Enrollment[];
    has_irrigation_system: boolean;
    active_crop_configuration: {
        id: number;
        crop_name: string;
        crop_key: string;
    };
}

export interface Enrollment {
    id: number;
    field: number;
    start_date: string;
    end_date: string;
    enrollment_tier: number;
    enrolling_organization: number;
    insights_start_date: string;
    insights_end_date: string;
    refunded_on: null;
    initiator_user: null;
    acres: number;
    retail_price_per_acre: string;
    date_enrolled: string;
    is_active: boolean;
    is_scheduled: boolean;
    credits: number;
    retail_refund_price_per_acre: string;
    refunded_value: string;
    enrolled_value: string;
    scheduled: boolean;
    insights_active: boolean;
}

export interface Contract {
    id: number;
    organization: number;
    contract_date: string;
    created_by: number;
    last_modified_by: number;
    line_items: Lineitem[];
    notes?: string;
    termination_date: string;
    is_terminated: boolean;
    is_active: boolean;
}

export interface Lineitem {
    id: number;
    acres: number;
    enrollment_tier: number;
    retail_price: string;
    contracted_price: string;
    contract_total: string;
    retail_total: string;
    discount: string;
}

export interface GDD {
    date: string;
    gdd: number;
    accumulated_gdd: number;
    growth_stage: string;
}

export interface BaseField {
    id: number;
    name: string;
    grower: number;
    service_provider: number;
    account_manager: number;
    active_planting_operation: {
        id: number;
        start_date: string;
        end_date: string;
        crop_configuration: {
            id: number;
            crop_name: string;
            crop_key: string;
        };
    };
    active_enrollment: {
        id: number;
        enrollment_tier: number;
        insights_active: boolean;
    };
    irrigation_system_configuration: {
        count: number;
        irrigated_acres: number;
        default_system: number;
    };
    has_drainage: boolean;
}

interface SoilLayer {
    SBDM: number;
    SCEC: number;
    SDUL: number;
    SLLL: number;
    SLOC: number;
    SRGF: number;
    SSAT: number;
}
export interface DSSATSoil {
    ALBEDO: number;
    LAYERS: {
        "5": SoilLayer;
        "15": SoilLayer;
        "30": SoilLayer;
        "60": SoilLayer;
        "100": SoilLayer;
        "200": SoilLayer;
    };
    DRAINAGE_RATE: number;
    RUNOFF_CURVE_NO: number;
    FERTILITY_FACTOR: number;
    S1_EVAPORATION_LIMIT: number;
    MINERALIZATION_FACTOR: number;
}

export interface LossFunctionParameters {
    w1: number;
    w2: number;
    w3: number;
    w4: number;
    w5: number;
    n_cost_per_lb: number;
    app_cost: number;
    crop_price: number;
    bu_per_n: number;
    niad_thresh: number;
    remob_thresh: number;
    roi_safe_frac: number;
    roi_gap_power: number;
}
